import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Settings, SettingsPatch, SettingsSection } from '@glyph/protocol-ts';
import { buildSettingsPatch, dirtySections } from '../settings/diff';
import { qk, useApi, useClient, useDeviceId } from './provider';

export function useSettings(id?: string) {
  const api = useApi();
  const deviceId = useDeviceId(id);
  return useQuery({ queryKey: qk.settings(deviceId), queryFn: () => api.settings(deviceId), enabled: !!deviceId });
}

export function useProfiles(id?: string) {
  const api = useApi();
  const qc = useQueryClient();
  const deviceId = useDeviceId(id);
  const q = useQuery({ queryKey: qk.profiles(deviceId), queryFn: () => api.profiles(deviceId) });
  const save = useMutation({ mutationFn: (name: string) => api.profileSave(deviceId, name), onSuccess: () => qc.invalidateQueries({ queryKey: qk.profiles(deviceId) }) });
  const load = useMutation({ mutationFn: (name: string) => api.profileLoad(deviceId, name), onSuccess: () => qc.invalidateQueries({ queryKey: qk.settings(deviceId) }) });
  return { ...q, save: save.mutateAsync, load: load.mutateAsync };
}

export interface SaveSettings {
  /** Server copy (undefined while loading). */
  saved: Settings | undefined;
  /** Editable draft; starts equal to saved and re-syncs clean sections when the server changes. */
  draft: Settings | undefined;
  dirty: SettingsSection[];
  isDirty: (section?: SettingsSection) => boolean;
  /** Shallow-merge into a section. Marks it dirty. */
  update: <K extends SettingsSection>(section: K, patch: Partial<Settings[K]>) => void;
  /** Replace a whole section. */
  replace: <K extends SettingsSection>(section: K, value: Settings[K]) => void;
  /** Revert one or all sections to the saved copy. */
  revert: (section?: SettingsSection) => void;
  /** settings.patch with only the dirty sections (optionally a subset). Masked secrets are never sent. */
  save: (sections?: SettingsSection[]) => Promise<string[]>;
  isSaving: boolean;
  error: Error | null;
  /** The patch that `save()` would send (for display.preview / tests). */
  pendingPatch: SettingsPatch;
}

/**
 * Port of the old dirty/save-bar semantics: per-section dirty flags, PATCH only changed sections,
 * masked secrets ("••••••••") dropped so they are never echoed back. The draft tracks the server for
 * sections that are not dirty (a settings_changed event from another operator updates clean sections).
 */
export function useSaveSettings(id?: string): SaveSettings {
  const api = useApi();
  const client = useClient();
  const qc = useQueryClient();
  const deviceId = useDeviceId(id);
  const q = useSettings(deviceId);
  const saved = q.data;
  const [draft, setDraft] = useState<Settings | undefined>(undefined);
  const dirtyRef = useRef<Set<SettingsSection>>(new Set());

  useEffect(() => {
    if (!saved) return;
    setDraft((d) => {
      if (!d) return saved;
      const next = { ...d };
      for (const k of Object.keys(saved) as (keyof Settings)[]) if (!dirtyRef.current.has(k as SettingsSection)) (next as any)[k] = saved[k];
      return next;
    });
  }, [saved]);

  useEffect(
    () =>
      client.subscribe(deviceId, ['settings_changed'], (ev) => {
        if (ev.topic === 'settings_changed') void qc.invalidateQueries({ queryKey: qk.settings(deviceId) });
      }),
    [client, deviceId, qc],
  );

  const dirty = useMemo(() => dirtySections(saved, draft), [saved, draft]);
  useEffect(() => {
    dirtyRef.current = new Set(dirty);
  }, [dirty]);

  const update = useCallback<SaveSettings['update']>((section, patch) => setDraft((d) => (d ? { ...d, [section]: { ...d[section], ...patch } } : d)), []);
  const replace = useCallback<SaveSettings['replace']>((section, value) => setDraft((d) => (d ? { ...d, [section]: value } : d)), []);
  const revert = useCallback(
    (section?: SettingsSection) => {
      if (!saved) return;
      setDraft((d) => (!d || !section ? saved : { ...d, [section]: saved[section] }));
    },
    [saved],
  );

  const mut = useMutation({
    mutationFn: async (sections?: SettingsSection[]) => {
      if (!saved || !draft) return [] as string[];
      const full = buildSettingsPatch(saved, draft);
      const patch: SettingsPatch = sections ? Object.fromEntries(Object.entries(full).filter(([k]) => sections.includes(k as SettingsSection))) : full;
      if (!Object.keys(patch).length) return [] as string[];
      const r = await api.patchSettings(deviceId, patch);
      return r.changed ?? Object.keys(patch);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: qk.settings(deviceId) });
    },
  });

  const pendingPatch = useMemo(() => (saved && draft ? buildSettingsPatch(saved, draft) : {}), [saved, draft]);

  return {
    saved,
    draft,
    dirty,
    isDirty: (s) => (s ? dirty.includes(s) : dirty.length > 0),
    update,
    replace,
    revert,
    save: (sections) => mut.mutateAsync(sections),
    isSaving: mut.isPending,
    error: (mut.error as Error | null) ?? (q.error as Error | null),
    pendingPatch,
  };
}
